import React, { useState, useEffect } from 'react';
import { Footer, Tooltip } from 'flowbite-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { HiOutlineGlobe, HiSun, HiMoon, HiInformationCircle, HiShieldCheck } from 'react-icons/hi';
import { FaLinkedin, FaFacebook, FaInstagram, FaYoutube, FaTelegram } from 'react-icons/fa';
import { FaXTwitter, FaCcVisa, FaCcMastercard, FaCcAmex, FaCcApplePay, FaGooglePay } from 'react-icons/fa6';
import { getFlagEmoji } from '../../utils/countryFlags';
import { purchaseService } from '../../services/purchaseService';

const WeChatPayIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M9.5 4C5.36 4 2 6.69 2 10c0 1.89 1.08 3.56 2.78 4.66l-.7 2.1 2.46-1.23c.78.22 1.6.34 2.46.34.34 0 .67-.02 1-.06A5.97 5.97 0 0 1 9.5 14c0-3.31 3.13-6 7-6 .17 0 .33.01.5.02C15.64 5.69 12.86 4 9.5 4zm-3 4a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5zm4 0a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5zM16.5 9c-3.58 0-6.5 2.46-6.5 5.5S12.92 20 16.5 20c.73 0 1.43-.1 2.09-.3l2.16 1.08-.62-1.85C21.36 17.9 23 16.36 23 14.5 23 11.46 20.08 9 16.5 9zm-2.25 4a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5zm4.5 0a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5z"/>
  </svg>
);

const NaverPayIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M3 3h18v18H3V3zm10.68 5.36v3.52l-2.44-3.52H8.16v7.28h3.08v-3.52l2.44 3.52h3.08V8.36h-3.08z"/>
  </svg>
);

const UsdcIcon = ({ className }) => (
  <svg viewBox="0 0 2000 2000" fill="currentColor" className={className}>
    <path fillRule="evenodd" d="M1000 0a1000 1000 0 1 0 0 2000 1000 1000 0 0 0 0-2000zm275 1158.33c0 104.17-83.34 191.67-208.34 212.5v100c0 20.84-16.66 33.34-33.33 33.34h-62.5c-20.83-4.17-33.33-16.67-33.33-33.34v-100c-133.34-20.83-195.84-95.83-216.67-195.83 0-16.67 12.5-29.17 29.17-29.17h70.83c12.5 0 25 8.33 29.17 25 12.5 62.5 50 108.33 158.33 108.33 79.17 0 141.67-45.83 141.67-112.5s-37.5-91.67-154.17-112.5c-175-20.83-258.33-75-258.33-212.5 0-104.17 79.17-187.5 204.17-204.17V604.16c4.16-20.83 16.66-33.33 33.33-33.33h62.5c20.83 4.16 33.33 16.66 33.33 33.33v100c91.67 12.5 166.67 83.33 183.34 175v4.16c0 16.67-12.5 29.17-29.17 29.17h-66.67c-12.5 0-25-8.33-29.17-20.83-16.66-62.5-54.16-87.5-120.83-87.5-75 0-112.5 37.5-112.5 95.83s25 91.67 150 108.34c175 20.83 262.5 70.83 262.5 216.66zM787.5 1595.83c-325-116.66-491.67-479.16-370.83-800 62.5-166.67 191.66-295.83 370.83-358.33 16.67-8.34 25-20.84 25-41.67v-58.33c0-16.67-8.33-29.17-25-33.34-4.17 0-12.5 0-16.67 4.17-395.83 125-612.5 545.83-487.5 941.67 75 237.5 262.5 425 487.5 500 16.67 8.33 33.34 0 37.5-16.67 4.17-4.17 4.17-8.33 4.17-16.67v-58.33c0-12.5-12.5-25-25-62.5zm441.67-1312.5c-16.67-8.33-33.34 0-37.5 16.67-4.17 4.17-4.17 8.33-4.17 16.67v58.33c0 16.67 12.5 29.17 25 41.67 325 116.67 491.67 479.17 370.83 800-62.5 166.67-191.66 295.83-370.83 358.34-16.67 8.33-25 20.83-25 41.66v58.34c0 16.66 8.33 29.16 25 33.33 4.17 0 12.5 0 16.67-4.17 395.83-125 612.5-545.83 487.5-941.67-75-241.66-266.67-429.16-487.5-479.16z"/>
  </svg>
);

const PageFooter = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isPublicPage = location.pathname === '/' || location.pathname.startsWith('/landing');
  const [darkMode, setDarkMode] = useState(false);
  const [commitInfo, setCommitInfo] = useState(null);
  const [detectedCountry, setDetectedCountry] = useState(null);
  const [detectionSource, setDetectionSource] = useState(null);

  // Check if we're in a development environment based on domain
  const currentDomain = window.location.hostname;
  const isDevelopment =
    currentDomain === 'jetogo.local' ||
    currentDomain === 'localhost' ||
    currentDomain === '127.0.0.1' ||
    currentDomain === 'dev.jetogo.com' ||
    import.meta.env.VITE_ENVIRONMENT === 'development';

  // Initialize dark mode from localStorage
  useEffect(() => {
    const savedDarkMode = localStorage.getItem('darkMode') === 'true';
    setDarkMode(savedDarkMode);
    if (savedDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, []);

  // Load commit info in development mode
  useEffect(() => {
    if (isDevelopment) {
      fetch('/commit-info.json')
        .then(response => response.json())
        .then(data => setCommitInfo(data))
        .catch(error => console.error('Failed to load commit info:', error));
    }
  }, [isDevelopment]);

  // Detect user's country via Cloudflare
  useEffect(() => {
    const detectCountry = async () => {
      try {
        const country = await purchaseService.detectHomeCountry();
        setDetectedCountry(country);

        // Get source from cache
        const cached = localStorage.getItem('home_country_cache');
        // Guard against the literal string "undefined" which localStorage
        // coerces from undefined values and passes the truthy check but
        // causes "JSON Parse error: Unexpected identifier 'undefined'" on Safari
        if (cached && cached !== 'undefined') {
          const { source } = JSON.parse(cached);
          setDetectionSource(source);
        }
      } catch (error) {
        console.error('Country detection failed:', error);
      }
    };

    detectCountry();
  }, []);

  // Toggle dark mode
  const toggleDarkMode = () => {
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);
    localStorage.setItem('darkMode', newDarkMode.toString());
    if (newDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  const handleCompareClick = (e) => {
    e.preventDefault();
    if (location.pathname === '/') {
      // Already on home page, just scroll
      const element = document.getElementById('comparison');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } else {
      // Navigate to home page, then scroll
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById('comparison');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  };

  return (
    <Footer container className="site-footer border-t bg-gradient-to-r from-[#216869] to-[#1a3d38] dark:from-[#1a3d38] dark:to-[#1f2421]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <div className="grid w-full justify-between sm:flex sm:justify-between md:flex md:grid-cols-1">
          <div className="mb-6 sm:mb-0">
            <Link to="/" className="flex items-center">
              <img src="/assets/images/brand/head_logo_wb_inverse.png" alt="Jetogo" className="h-10" style={{width: 'auto'}} />
            </Link>
            <p className="mt-2 text-sm text-gray-300">Your global connectivity solution.</p>
            <div className="mt-3 flex items-center gap-4">
              <Tooltip content="Follow on LinkedIn" style="dark">
                <a href="https://www.linkedin.com/company/jetogo/" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-white">
                  <FaLinkedin className="h-5 w-5" />
                </a>
              </Tooltip>
              <Tooltip content="Follow on Facebook" style="dark">
                <a href="https://www.facebook.com/people/Jetogo/61584113725583/" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-white">
                  <FaFacebook className="h-5 w-5" />
                </a>
              </Tooltip>
              <Tooltip content="Follow on Instagram" style="dark">
                <a href="https://www.instagram.com/jetogo.global/" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-white">
                  <FaInstagram className="h-5 w-5" />
                </a>
              </Tooltip>
              <Tooltip content="Follow on X" style="dark">
                <a href="https://x.com/_jetogo" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-white">
                  <FaXTwitter className="h-5 w-5" />
                </a>
              </Tooltip>
              <Tooltip content="Follow on YouTube" style="dark">
                <a href="https://www.youtube.com/@jetogo-esim" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-white">
                  <FaYoutube className="h-5 w-5" />
                </a>
              </Tooltip>
              <Tooltip content="Telegram AI Bot" style="dark">
                <a href="https://t.me/jetogo_bot" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-white">
                  <FaTelegram className="h-5 w-5" />
                </a>
              </Tooltip>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-8 sm:mt-4 sm:grid-cols-4 sm:gap-6">
            <div>
              <Footer.Title title="Pricing" />
              <Footer.LinkGroup col>
                <Link to="/countries">Countries</Link>
              </Footer.LinkGroup>
            </div>
            {isPublicPage ? (
              <div>
                <Footer.Title title="Plans" />
                <Footer.LinkGroup col>
                  <Link to="/landing/pay-as-you-go">Pay as You Go</Link>
                  <Link to="/landing/subscription-plans">Subscription Plans</Link>
                  <Link to="/landing/business-plans">Business Plans</Link>
                  <a href="/#comparison" onClick={handleCompareClick} className="cursor-pointer">Compare Plans</a>
                </Footer.LinkGroup>
              </div>
            ) : (
              <div>
                <Footer.Title title="Account" />
                <Footer.LinkGroup col>
                  <Link to="/dashboard">Dashboard</Link>
                  <Link to="/dashboard/devices">Manage Devices</Link>
                  <Link to="/dashboard/usage">Usage Data</Link>
                  <Link to="/dashboard/billing">Billing</Link>
                </Footer.LinkGroup>
              </div>
            )}
            <div>
              <Footer.Title title="Resources" />
              <Footer.LinkGroup col>
                <Link to="/help">AI Support Chat</Link>
                <Link to="/contact">Contact Us</Link>
                <Link to="/faq">FAQs</Link>
                <Footer.Link href="https://docs.jetogo.com/docs">Docs</Footer.Link>
                <Link to="/news">Blog</Link>
                <Link to="/affiliate">Affiliate Program</Link>
              </Footer.LinkGroup>
            </div>
            <div>
              <Footer.Title title="Legal" />
              <Footer.LinkGroup col>
                <Link to="/privacy">Privacy Policy</Link>
                <Link to="/terms">Terms &amp; Conditions</Link>
                <Link to="/refund-policy">Refund Policy</Link>
                {isPublicPage && (
                  <>
                    <div className="mt-6"></div>
                    <Link to="/dashboard">Login</Link>
                    <Link to="/newplan">Sign Up</Link>
                  </>
                )}
              </Footer.LinkGroup>
            </div>
          </div>
        </div>
        <Footer.Divider />
        <div className="w-full sm:flex sm:items-center sm:justify-between">
          {isDevelopment ? (
            <div className="flex flex-wrap items-center gap-2 text-xs text-gray-300">
              <span>© {new Date().getFullYear()} Jetogo</span>
              <span className="text-emerald-300 font-medium font-mono bg-white/10 px-1 rounded">Dev Server</span>
              <span className="text-gray-400">|</span>
              <Tooltip content="Add any suggestions, errors, screenshots or problems faced here" style="dark">
                <a
                  href={`https://jetogo.canny.io/features-and-fixes?details=${encodeURIComponent(`Reported from: ${window.location.href}`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-300 hover:text-white transition-colors font-mono bg-white/10 px-1 rounded"
                >
                  Make Suggestion
                </a>
              </Tooltip>
              <span className="text-gray-400">|</span>
              <Tooltip content="Here is what we are working on!" style="dark">
                <a
                  href="https://jetogo.canny.io/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-300 hover:text-white transition-colors font-mono bg-white/10 px-1 rounded"
                >
                  Roadmap
                </a>
              </Tooltip>
              <span className="text-gray-400">|</span>
              <Tooltip content="See what has been released!" style="dark">
                <a
                  href="https://jetogo.canny.io/changelog"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-300 hover:text-white transition-colors font-mono bg-white/10 px-1 rounded"
                >
                  Changelog
                </a>
              </Tooltip>
              <span className="text-gray-400">|</span>
              <Tooltip content="System status and uptime" style="dark">
                <a
                  href="https://jetogo.statuspage.io/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-300 hover:text-white transition-colors font-mono bg-white/10 px-1 rounded"
                >
                  Status
                </a>
              </Tooltip>

              {commitInfo && (
                <>
                  <span className="text-gray-400">|</span>
                  <span className="text-gray-300 font-mono bg-white/10 px-1 rounded">Last Update:</span>
                  <Tooltip
                    content={
                      <div className="max-w-md text-xs">
                        <div className="font-semibold mb-1">{commitInfo.title}</div>
                        {commitInfo.body && (
                          <div className="whitespace-pre-wrap text-gray-300">{commitInfo.body}</div>
                        )}
                      </div>
                    }
                    style="dark"
                  >
                    <span className="text-gray-300 cursor-help font-mono bg-white/10 px-1 rounded">
                      {commitInfo.date}
                    </span>
                  </Tooltip>
                </>
              )}
            </div>
          ) : (
            <span className="text-sm text-gray-300">© {new Date().getFullYear()} Jetogo</span>
          )}
          <div className="mt-4 flex items-center space-x-4 sm:mt-0 sm:justify-center">
            {/* Accepted Payment Methods */}
            <div className="flex items-center gap-2 text-gray-400">
              <Tooltip content="Visa" style="dark"><FaCcVisa className="h-6 w-6" /></Tooltip>
              <Tooltip content="Mastercard" style="dark"><FaCcMastercard className="h-6 w-6" /></Tooltip>
              <Tooltip content="American Express" style="dark"><FaCcAmex className="h-6 w-6" /></Tooltip>
              <Tooltip content="Apple Pay" style="dark"><FaCcApplePay className="h-6 w-6" /></Tooltip>
              <Tooltip content="Google Pay" style="dark"><FaGooglePay className="h-6 w-6" /></Tooltip>
              <Tooltip content="WeChat Pay" style="dark"><WeChatPayIcon className="h-6 w-6" /></Tooltip>
              <Tooltip content="Naver Pay" style="dark"><NaverPayIcon className="h-6 w-6" /></Tooltip>
              <Tooltip content="USDC" style="dark"><UsdcIcon className="h-6 w-6" /></Tooltip>
              <Tooltip content="Secure Payments" style="dark"><HiShieldCheck className="h-6 w-6" /></Tooltip>
            </div>
            {/* Detected Country Flag */}
            {detectedCountry && (
              <Tooltip
                content={
                  <div className="text-xs">
                    <div className="font-semibold">Detected Location: {detectedCountry}</div>
                    <div className="text-gray-400 mt-1">
                      Source: {detectionSource === 'cloudflare' ? '🔵 Cloudflare' : detectionSource === 'ipapi' ? '🟢 ipapi.co' : '⚪ Default'}
                    </div>
                  </div>
                }
                style="dark"
              >
                <div className="flex items-center text-2xl cursor-help">
                  {getFlagEmoji(detectedCountry)}
                </div>
              </Tooltip>
            )}

            {/* Dark Mode Toggle */}
            <button
              onClick={toggleDarkMode}
              className="p-2 rounded-lg text-gray-300 hover:text-white hover:bg-white/10 transition-colors duration-200"
              aria-label="Toggle dark mode"
            >
              {darkMode ? (
                <HiSun className="h-5 w-5" />
              ) : (
                <HiMoon className="h-5 w-5" />
              )}
            </button>
          </div>
        </div>
      </div>
    </Footer>
  );
};

export default PageFooter;